<template>
  <validation-observer tag="form" @submit.native.prevent ref="validation">

    <fragment>
      <div class="card-body">
          <h2>Fault History</h2>

          <p class="text-muted mb-0">
            Generate a detailed history of faults recorded for screens.
          </p>
      </div>

      <ul class="filters">
          <li>
            <div class="row">
              <label class="col-lg-3 col-form-label">Date range</label>
              <div class="col-lg-9">
                  <validation-provider name="date range" vid="date_range" rules="required" v-slot="{ failed }">
                    <input-date-range
                        v-model="filters.dateRange"
                        placeholder=""
                        class="form-control date-range"
                        :class="{ 'is-invalid': failed }"
                      />
                      <c-help-block />
                  </validation-provider>
              </div>
            </div><!-- /.row -->
          </li>
          <li v-if="$isA('admin')">
              <div class="row">
                  <label class="col-lg-3 col-form-label">Media owner</label>
                  <div class="col-lg-9">
                      <validation-provider name="media owners" vid="media_owners" rules="required" v-slot="{ failed }">
                        <multiselect
                            v-model="filters.mediaOwner"
                            placeholder="Select"
                            :preselect-first="false"
                            :show-labels="false"
                            label="name"
                            track-by="id"
                            :options="mediaOwners"
                            :class="{ 'is-invalid': failed }"
                        />
                        <c-help-block />
                    </validation-provider>
                  </div>
              </div><!-- /.row -->
          </li>

          <li>
              <div class="row">
                  <label class="col-lg-3 col-form-label">Screen</label>
                  <div class="col-lg-9">
                    <validation-provider name="screen" vid="screen" rules="required" v-slot="{ failed }">
                      <c-input
                        as="multiselect"
                        v-model="selectedScreen"
                        :options="screensOptions"
                        :preselect-first="true"
                        :show-labels="false"
                        :allow-empty="false"
                        :class="{ 'is-invalid': failed }"
                        label="label"
                        track-by="name"
                      />
                      <c-help-block />
                    </validation-provider>
                  </div>
              </div><!-- /.row -->
          </li>
      </ul><!-- /.filters -->

      <div class="card-body border-top text-end mt-auto">
          <c-button type="submit" variant="primary" @click="handleGenerateReport('csv')" validate class="ms-auto me-2" >
            Download CSV
          </c-button>
          <c-button type="submit" variant="primary" @click="handleGenerateReport('pdf')" validate class="ms-auto mr-2" >
            Download PDF
          </c-button>
      </div>

    </fragment>
  </validation-observer>
</template>

<script>
import { toast } from '@chameleon/chameleon-vue'
import { http } from '@/utils'
import { get, call } from 'vuex-pathify'
import dayjs from 'dayjs'
import InputDateRange from '@/components/forms/InputDateRange'
import qs from 'qs'
export default {
  components: {
    InputDateRange
  },
  data () {
    return {
      filters: {
        mediaOwner: null,
        screen: null,
        dateRange: null,
      }
    }
  },

  async created () {
    try {
      const params = !this.$isA('admin') && this.$user.media_owner_id ? { params: { mediaOwner: this.$user.media_owner_id } } : {}

      await this.getMediaOwners()
      await this.getScreens(params)
    } catch (e) {
      toast('We encountered an error. We have been alerted and will investigate.', 'danger')
      throw e
    }
  },

  computed: {
    mediaOwners: get('lists/mediaOwners/mediaOwners'),
    screens: get('lists/screens/screens'),

    hideScreen () {
      if (this.$isA('admin')) {
        return this.mediaOwner !== null
      } else {
        return true
      }
    },

    screensOptions () {
      return [{ id: null, name: 'all', label: 'All' }, ...this.screens.map((screen) => ({ id: screen.id, name: screen.name, label: screen.name }))]
    },

    selectedScreen: {
      get () {
        return this.screensOptions.find(({ name }) => name === this.filters?.screen?.name)
      },
      set (value) {
        this.filters.screen = value
      }
    }
  },

  watch: {
    filters: {
      async handler (newFilters) {
        const params = {}

        if (this.$isA('admin')) {
          if (newFilters.mediaOwner) {
            params.mediaOwner = newFilters.mediaOwner.id
          }
        } else if (this.$user.media_owner_id) {
          params.mediaOwner = this.$user.media_owner_id
        }

        await this.getScreens(Object.keys(params).length ? { params } : undefined)
      },
      deep: true
    }
  },

  methods: {
    getMediaOwners: call('lists/mediaOwners/getMediaOwners'),
    getScreens: call('lists/screens/getScreens'),

    handleGenerateReport (type) {
      const params = {
        mediaOwner: this.filters?.mediaOwner?.id ?? null,
        screen: this.filters.screen?.id ? this.filters.screen.id : null,
        startDate: this.filters.dateRange.startDate,
        endDate: this.filters.dateRange.endDate,
        type: type
      }
      return http
        .get('reports/fault-history', {
          responseType: 'blob',
          headers: { accept: 'application/json, text/csv' },
          params,
          paramsSerializer: params => qs.stringify(params, {
            skipNulls: false, // Turn off skipping null values
            encode: true
          })
        })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          const parts = ['FaultHistory']

          if (this.$isA('admin') && this.filters.mediaOwner) {
            const sanitized = this.filters.mediaOwner.name.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9_-]/g, '')
            parts.push(sanitized)
          }

          if (this.filters.dateRange && this.filters.dateRange.startDate && this.filters.dateRange.endDate) {
            const startDate = dayjs(this.filters.dateRange.startDate).format('YYYYMMDD')
            const endDate = dayjs(this.filters.dateRange.endDate).format('YYYYMMDD')
            parts.push(`${startDate}-${endDate}`)
          } else {
            parts.push('AllTime')
          }

          const filename = `${parts.join('-')}.${type}`
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          return response
        })
        .catch(e => {
          toast('We encountered an error. We have been alerted and will investigate.', 'danger')
          throw e
        })
    }
  }
}
</script>
